import { Button, Layout, Tag } from '@loadsmart/miranda-react'
import { getToken } from '@loadsmart/miranda-react/dist/tokens'

import { formatLocation, formatStopAppointmentDate } from 'core/presenters/stopPresenter'
import { ShipmentTags } from 'core/shipment-tag/ui/ShipmentTags'
import { CardInfoItem } from 'core/ui/components/card/CardInfoItem'
import { VerticalDivider } from 'core/ui/components/divider/VerticalDivider'
import { ShipperInformation } from 'core/ui/components/shipper/ShipperInformation'

import { formatEquipment } from '../presenters/agnosticShipmentPresenter'
import { LastLocation } from './LastLocation'
import { type ShipmentCardProps } from './ShipmentCard'
import { ShipmentIdentifiers } from './ShipmentIdentifiers'
import { ShipmentListChat } from './ShipmentListChat'
import { StopInformation } from './StopInformation'

export function ShipmentCardDesktop({
  shipment,
  getActionLabel,
  getIdentifiers,
  getStatus,
  onActionClick,
}: ShipmentCardProps) {
  const actionLabel = getActionLabel?.()
  const identifiers = getIdentifiers?.()
  const { label, variant } = getStatus?.() ?? {}

  const {
    equipmentType,
    mileage,
    mode,
    shipper,
    stops,
    equipmentLength,
    equipmentSubType,
    poNumbers,
    soNumbers,
    bolNumber,
  } = shipment

  function handleOnActionClick(event: React.MouseEvent) {
    event.stopPropagation()
    onActionClick?.()
  }

  return (
    <Layout.Box
      backgroundColor="color-background-primary"
      borderRadius="border-radius-s"
      borderWidth="border-thin"
      borderColor="color-border"
      padding="none"
      style={{ display: 'block' }}
      data-testid="shipment-card-desktop"
    >
      <Layout.Group style={{ padding: getToken('spacing-6'), flexWrap: 'nowrap' }}>
        <Layout.Stack gap="spacing-6" style={{ flexGrow: 1 }}>
          <Layout.Group justify="space-between" align="center" style={{ flexWrap: 'nowrap' }}>
            <StopInformation
              stops={stops}
              distance={mileage}
              locationFormatter={formatLocation}
              dateFormatter={formatStopAppointmentDate}
              distanceUnit="miles"
              style={{ flexGrow: 1, flexShrink: 0 }}
            />
            <Layout.Stack align="flex-end" justify="space-between">
              <Layout.Group justify="space-between" align="center" style={{ flexWrap: 'nowrap' }}>
                <Layout.Group align="center" justify="flex-end">
                  <ShipmentTags tags={shipment.tags} />
                  <ShipmentIdentifiers identifiers={identifiers} />
                  <Tag variant={variant}>{label}</Tag>
                  <ShipmentListChat shipment={shipment} />
                </Layout.Group>
              </Layout.Group>
              {actionLabel && (
                <Button
                  variant="secondary"
                  type="button"
                  onClick={handleOnActionClick}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {actionLabel}
                </Button>
              )}
            </Layout.Stack>
          </Layout.Group>
          <Layout.Group gap="spacing-6" style={{ flexWrap: 'nowrap' }}>
            <ShipperInformation
              companyName={shipper.companyName}
              logo={shipper.logoUrl}
              showContactInfo={false}
            />
            <VerticalDivider />
            <CardInfoItem title="Mode" value={mode} />
            <VerticalDivider />
            <CardInfoItem
              title="Equipment"
              value={formatEquipment(equipmentType, equipmentLength)}
            />
            {equipmentSubType && (
              <>
                <VerticalDivider />
                <CardInfoItem title="Sub Equipment" value={equipmentSubType} />
              </>
            )}
            {bolNumber && (
              <>
                <VerticalDivider />
                <CardInfoItem title="BOL#" value={bolNumber} />
              </>
            )}
            {poNumbers && (
              <>
                <VerticalDivider />
                <CardInfoItem
                  style={{ maxWidth: '110px', flexWrap: 'wrap', overflowWrap: 'anywhere' }}
                  title="PO#"
                  value={poNumbers.join(', ')}
                />
              </>
            )}
            {soNumbers && (
              <>
                <VerticalDivider />
                <CardInfoItem
                  style={{ maxWidth: '110px', flexWrap: 'wrap', overflowWrap: 'anywhere' }}
                  title="SO#"
                  value={soNumbers.join(', ')}
                />
              </>
            )}
          </Layout.Group>
        </Layout.Stack>
      </Layout.Group>
      <LastLocation shipment={shipment} />
    </Layout.Box>
  )
}
